import React, { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { showHtdLoginSuccess } from '../utils/htdLoginSuccess'
import type { TenantThemeConfig } from '../types/theme'
import * as authService from '../services/authService'
import { getThemeById } from '../config/themes'
import './ModernLoginPage.css'

interface ModernLoginPageProps {
  themeConfig: TenantThemeConfig | null
}

const REMEMBER_ME_KEY = 'rememberMe'
const SAVED_USERNAME_KEY = 'savedUsername'
const AUTH_NOTICE_KEY = 'htd_auth_notice'

type AuthNotice = 'expired' | 'replaced' | null

function readAuthNotice(): AuthNotice {
  try {
    const raw = sessionStorage.getItem(AUTH_NOTICE_KEY)
    sessionStorage.removeItem(AUTH_NOTICE_KEY)
    if (raw === 'expired' || raw === 'replaced') return raw
  } catch {
    /* ignore */
  }
  return null
}

/** หน้า Login — โทน HTD (full-bleed) รองรับกลับจาก session หมดอายุ */
const ModernLoginPage: React.FC<ModernLoginPageProps> = ({ themeConfig }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const fromPath =
    (location.state as { from?: string } | null)?.from ||
    '/'

  const [phone, setPhone] = useState(
    () => (typeof localStorage !== 'undefined' ? localStorage.getItem(SAVED_USERNAME_KEY) || '' : '')
  )
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [loginError, setLoginError] = useState('')
  const [authNotice, setAuthNotice] = useState<AuthNotice>(() => readAuthNotice())
  const [rememberMe, setRememberMe] = useState(
    () =>
      typeof localStorage !== 'undefined' &&
      (localStorage.getItem(REMEMBER_ME_KEY) !== 'false' || !!localStorage.getItem(SAVED_USERNAME_KEY))
  )

  useEffect(() => {
    document.body.classList.add('htd-login-active')
    return () => document.body.classList.remove('htd-login-active')
  }, [])

  useEffect(() => {
    if (authService.isAuthenticated()) {
      navigate(fromPath.startsWith('/') ? fromPath : '/', { replace: true })
    }
  }, [navigate, fromPath])

  const siteName = themeConfig?.theme?.branding?.siteName || 'หวยทูเดย์'
  const logoUrl = themeConfig?.theme?.branding?.logoUrl

  const bgUrl = useMemo(() => {
    if (!themeConfig) return ''
    const folder = getThemeById(themeConfig.themeId)?.folder ?? 'HuayToday-Blue'
    const cdn =
      (import.meta.env as { VITE_CDN_URL?: string }).VITE_CDN_URL ||
      'https://huay-cdn-assets-prod.s3.ap-southeast-1.amazonaws.com'
    return `${cdn}/${folder}/img/bg.jpg`
  }, [themeConfig])

  const handleLogin = async (e?: React.FormEvent) => {
    e?.preventDefault()
    const identifier = phone.trim()
    const secret = password

    if (!identifier || !secret || !themeConfig?.tenantId || submitting) return

    setSubmitting(true)
    setLoginError('')
    setAuthNotice(null)

    try {
      const res = await authService.login({
        tenantId: themeConfig.tenantId,
        username: identifier,
        password: secret,
        rememberMe,
      })

      if (res.success) {
        if (typeof localStorage !== 'undefined') {
          if (rememberMe) localStorage.setItem(SAVED_USERNAME_KEY, identifier)
          else localStorage.removeItem(SAVED_USERNAME_KEY)
          localStorage.setItem(REMEMBER_ME_KEY, rememberMe ? 'true' : 'false')
        }

        await showHtdLoginSuccess({
          username: identifier,
          timerMs: 1400,
          siteName,
        })

        const dest = fromPath && fromPath !== '/login' ? fromPath : '/'
        navigate(dest, { replace: true })
        window.location.reload()
        return
      }

      const msg =
        res.message ||
        res.error ||
        (res.code === 'CONCURRENT_LOGIN_BLOCKED'
          ? 'บัญชีนี้เข้าสู่ระบบจากอุปกรณ์อื่นอยู่'
          : 'เข้าสู่ระบบไม่สำเร็จ')
      setLoginError(msg)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'เกิดข้อผิดพลาด'
      setLoginError(msg)
      await Swal.fire({
        icon: 'error',
        title: 'เข้าสู่ระบบไม่สำเร็จ',
        text: msg,
        confirmButtonColor: 'var(--htd-primary)',
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (!themeConfig) {
    return (
      <div className="htd-login htd-login--loading">
        <p>กำลังโหลด...</p>
      </div>
    )
  }

  return (
    <div
      className="htd-login"
      style={bgUrl ? ({ ['--htd-login-bg' as string]: `url(${bgUrl})` } as React.CSSProperties) : undefined}
    >
      <div className="htd-login__ambient" aria-hidden>
        <span className="htd-login__orb htd-login__orb--a" />
        <span className="htd-login__orb htd-login__orb--b" />
      </div>

      <div className="htd-login__shell">
        <Link to="/" className="htd-login__brand" aria-label={siteName}>
          {logoUrl ? <img src={logoUrl} alt={siteName} /> : <span>{siteName}</span>}
        </Link>

        <form className="htd-login__card" onSubmit={handleLogin} noValidate>
          <h1 className="htd-login__title">เข้าสู่ระบบ</h1>
          <p className="htd-login__sub">พร้อมแทงหวยได้ทันทีหลังเข้าสู่ระบบ</p>

          {authNotice === 'expired' ? (
            <p className="htd-login__notice" role="status">
              เซสชันหมดอายุแล้ว กรุณาเข้าสู่ระบบใหม่
            </p>
          ) : null}
          {authNotice === 'replaced' ? (
            <p className="htd-login__notice" role="status">
              มีการเข้าสู่ระบบจากอุปกรณ์อื่น กรุณาเข้าสู่ระบบใหม่
            </p>
          ) : null}
          {loginError ? <p className="htd-login__error">{loginError}</p> : null}

          <label className="htd-login__field" htmlFor="login-identifier">
            <span>เบอร์โทร หรือ Username</span>
            <div className="htd-login__input-wrap">
              <i className="fas fa-mobile-alt" aria-hidden />
              <input
                id="login-identifier"
                type="text"
                inputMode="text"
                autoComplete="username"
                placeholder="ชื่อผู้ใช้ หรือเบอร์โทร"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={submitting}
              />
            </div>
          </label>

          <label className="htd-login__field" htmlFor="login-password">
            <span>รหัสผ่าน</span>
            <div className="htd-login__input-wrap">
              <i className="fas fa-lock" aria-hidden />
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="รหัสผ่าน"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
              />
              <button
                type="button"
                className="htd-login__eye"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
                tabIndex={-1}
              >
                <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden />
              </button>
            </div>
          </label>

          <div className="htd-login__meta">
            <label className="htd-login__remember">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => {
                  const v = e.target.checked
                  setRememberMe(v)
                  if (typeof localStorage !== 'undefined') {
                    localStorage.setItem(REMEMBER_ME_KEY, v ? 'true' : 'false')
                  }
                }}
              />
              จำฉันไว้ในระบบ
            </label>
            <Link to="/contact" className="htd-login__forgot" title="ติดต่อแอดมินเพื่อรีเซ็ตรหัสผ่าน">
              ลืมรหัสผ่าน
            </Link>
          </div>

          <button
            type="submit"
            className="htd-login__submit"
            disabled={submitting || !phone.trim() || !password}
          >
            {submitting ? 'กำลังเข้าสู่ระบบ...' : 'เข้าสู่ระบบ'}
          </button>

          {themeConfig.settings?.lineLoginEnabled !== false ? (
            <button
              type="button"
              className="htd-login__line"
              onClick={() => {
                void Swal.fire({
                  icon: 'info',
                  title: 'เร็ว ๆ นี้',
                  text: 'ระบบเข้าสู่ระบบด้วย LINE กำลังเตรียมเปิดให้บริการ',
                  confirmButtonColor: 'var(--htd-primary)',
                })
              }}
            >
              <i className="fab fa-line" aria-hidden />
              เข้าสู่ระบบด้วย LINE
            </button>
          ) : null}

          <p className="htd-login__foot">
            ยังไม่มีบัญชี? <Link to="/register">สมัครสมาชิก</Link>
          </p>

          <Link to="/" className="htd-login__back">
            <i className="fas fa-chevron-left" aria-hidden />
            กลับหน้าแรก
          </Link>
        </form>
      </div>
    </div>
  )
}

export default ModernLoginPage
